import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import API from "../api";
import BackButton from "./common/BackButton";
import { SEMANTIC } from "../styles/theme";
import { useAppModal } from "../hooks/useAppModal";

// ======================================================
// Mr. K — chat drawer inline
// ======================================================
function MrKDrawer({ open, onClose }) {
  const userId = localStorage.getItem("userId");
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [booted, setBooted] = useState(false);
  const bottomRef = useRef(null);

  // Scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  // Invia il messaggio iniziale automatico alla prima apertura
  useEffect(() => {
    if (open && !booted) {
      setBooted(true);
      sendMessage(t("portfolio_type_selector.mrk_initial_message"));
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const sendMessage = async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: q }]);
    setLoading(true);
    try {
      const history = messages.filter(m => m.role === "user" || m.role === "assistant");
      const res = await API.post("/ai/advisor", {
        user_id: Number(userId),
        question: q,
        history,
      });
      setMessages(prev => [...prev, { role: "assistant", content: res.data.answer || "" }]);
    } catch (e) {
      setMessages(prev => [...prev, { role: "error", content: e.response?.data?.error || "Errore nella risposta AI" }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      {open && (
        <div
          onClick={onClose}
          style={{
            position: "fixed", inset: 0,
            backgroundColor: "rgba(0,0,0,0.55)",
            zIndex: 999,
          }}
        />
      )}

      {/* Drawer */}
      <div style={{
        position: "fixed",
        bottom: 0, left: 0, right: 0,
        height: "72vh",
        backgroundColor: SEMANTIC.backgroundCard,
        borderRadius: "20px 20px 0 0",
        boxShadow: "0 -8px 40px rgba(0,0,0,0.6)",
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        transform: open ? "translateY(0)" : "translateY(100%)",
        transition: "transform 0.35s cubic-bezier(0.32,0.72,0,1)",
      }}>
        {/* Handle + header */}
        <div style={{
          padding: "14px 20px 12px",
          borderBottom: "1px solid rgba(255,215,0,0.12)",
          flexShrink: 0,
        }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 10 }}>
            <div style={{ width: 36, height: 4, borderRadius: 2, backgroundColor: "rgba(255,255,255,0.15)" }} />
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 24 }}>🪐</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, color: "#ffd700" }}>Mr. K — AI Advisor</div>
                <div style={{ fontSize: 11, color: "#888" }}>Ti aiuto a scegliere il portafoglio più adatto</div>
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                background: "none", border: "none", color: "#888",
                fontSize: 20, cursor: "pointer", padding: "4px 8px", lineHeight: 1,
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Messaggi */}
        <div style={{
          flex: 1, overflowY: "auto",
          padding: "16px 16px 0",
          display: "flex", flexDirection: "column", gap: 12,
        }}>
          {messages.map((msg, i) => (
            <div key={i} style={{ display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start" }}>
              <div style={{
                maxWidth: "85%", padding: "10px 14px", fontSize: 14, lineHeight: 1.6,
                whiteSpace: "pre-wrap",
                borderRadius: msg.role === "user" ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                ...(msg.role === "user" ? {
                  backgroundColor: "rgba(255,215,0,0.13)",
                  border: "1px solid rgba(255,215,0,0.28)",
                  color: SEMANTIC.textPrimary,
                } : msg.role === "error" ? {
                  backgroundColor: "rgba(255,68,68,0.1)",
                  border: "1px solid rgba(255,68,68,0.3)",
                  color: "#ff6666",
                } : {
                  backgroundColor: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  color: SEMANTIC.textPrimary,
                }),
              }}>
                {msg.role === "assistant" && (
                  <div style={{ fontSize: 10, color: "#ffd700", marginBottom: 6, fontWeight: 700 }}>
                    🪐 Mr. K
                  </div>
                )}
                {msg.content}
              </div>
            </div>
          ))}

          {loading && (
            <div style={{ display: "flex", justifyContent: "flex-start" }}>
              <div style={{
                padding: "10px 16px", borderRadius: "16px 16px 16px 4px",
                backgroundColor: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.08)",
                display: "flex", gap: 5, alignItems: "center",
              }}>
                {[0, 1, 2].map(i => (
                  <div key={i} style={{
                    width: 7, height: 7, borderRadius: "50%",
                    backgroundColor: "#ffd700",
                    animation: `mrk-bounce 1.2s ease-in-out ${i * 0.2}s infinite`,
                    opacity: 0.7,
                  }} />
                ))}
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <div style={{
          padding: "12px 16px",
          borderTop: "1px solid rgba(255,255,255,0.08)",
          backgroundColor: SEMANTIC.backgroundCard,
          display: "flex", gap: 8, alignItems: "center",
          flexShrink: 0,
        }}>
          <input
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && sendMessage()}
            placeholder="Scrivi un messaggio..."
            disabled={loading}
            style={{
              flex: 1, padding: "10px 14px", borderRadius: 22,
              border: "1px solid rgba(255,215,0,0.25)",
              backgroundColor: "rgba(255,255,255,0.05)",
              color: SEMANTIC.textPrimary, fontSize: 14, outline: "none",
            }}
          />
          <button
            onClick={() => sendMessage()}
            disabled={loading || !input.trim()}
            style={{
              width: 42, height: 42, borderRadius: "50%", border: "none",
              backgroundColor: loading || !input.trim() ? "rgba(255,215,0,0.2)" : "#ffd700",
              color: loading || !input.trim() ? "#888" : "#000",
              cursor: loading || !input.trim() ? "not-allowed" : "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 16, flexShrink: 0,
            }}
          >
            <i className="bi bi-send-fill" />
          </button>
        </div>
      </div>

      <style>{`
        @keyframes mrk-bounce {
          0%, 60%, 100% { transform: translateY(0); }
          30% { transform: translateY(-6px); }
        }
      `}</style>
    </>
  );
}

// ======================================================
// Asset type badge helper
// ======================================================
const ASSET_TYPE_CFG = {
  STOCK:  { label: 'Stocks',  color: '#00aaff', icon: 'bi-bar-chart-fill' },
  ETF:    { label: 'ETF',     color: '#ffaa00', icon: 'bi-collection-fill' },
  BOND:   { label: 'Bond',    color: '#00ff88', icon: 'bi-shield-fill' },
  CRYPTO: { label: 'Crypto',  color: '#aa00ff', icon: 'bi-currency-bitcoin' },
  ALL:    { label: 'Multi',   color: '#889aaa', icon: 'bi-grid-fill' },
};

function AssetTypeBadge({ universe, size = 'md' }) {
  const cfg = ASSET_TYPE_CFG[(universe || 'ALL').toUpperCase()] || ASSET_TYPE_CFG.ALL;
  const pad = size === 'sm' ? '2px 8px' : '4px 12px';
  const fs  = size === 'sm' ? 10 : 12;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: pad, borderRadius: 20,
      fontSize: fs, fontWeight: 700, letterSpacing: 0.4,
      backgroundColor: `${cfg.color}18`,
      color: cfg.color,
      border: `1px solid ${cfg.color}44`,
    }}>
      <i className={`bi ${cfg.icon}`} style={{ fontSize: fs }} />
      {cfg.label}
    </span>
  );
}

// ======================================================
// Componente: scala a pallini (1-N)
// ======================================================
function ScaleIndicator({ value, max, color }) {
  return (
    <div style={{ display: "flex", gap: "3px" }}>
      {Array.from({ length: max }, (_, i) => (
        <div
          key={i}
          style={{
            width: "9px",
            height: "9px",
            borderRadius: "50%",
            backgroundColor: i < value ? color : "rgba(255,255,255,0.1)",
            border: `1px solid ${color}`,
            transition: "background-color 0.2s",
          }}
        />
      ))}
    </div>
  );
}

// ======================================================
// Modale dettagli tipo portafoglio
// ======================================================
const PERF_PERIODS_MODAL = [
  ["3 mesi",  "perf_3m",  "sp500_diff_3m"],
  ["6 mesi",  "perf_6m",  "sp500_diff_6m"],
  ["12 mesi", "perf_12m", "sp500_diff_12m"],
  ["24 mesi", "perf_24m", "sp500_diff_24m"],
];
const INDICATOR_DEFS_MODAL = [
  ["alpha",           "Alpha",       "alpha"],
  ["beta",            "Beta",        "beta"],
  ["sharpe_ratio",    "Sharpe Ratio","sharpe"],
  ["sharpe_ratio_3m", "Sharpe 3m",   "sharpe"],
  ["sharpe_ratio_6m", "Sharpe 6m",   "sharpe"],
  ["sharpe_ratio_12m","Sharpe 12m",  "sharpe"],
  ["sharpe_ratio_24m","Sharpe 24m",  "sharpe"],
];
const INTERVALLO_MODAL = { 1: "3 mesi", 2: "6 mesi", 3: "12 mesi", 4: "24 mesi", 5: "36+ mesi" };

function PortfolioDetailModal({ tipo, onClose }) {
  if (!tipo) return null;

  const hasPerfData  = PERF_PERIODS_MODAL.some(([, k]) => tipo[k] != null);
  const hasIndData   = INDICATOR_DEFS_MODAL.some(([k]) => tipo[k] != null);

  const sectionHead  = { color: SEMANTIC.actionBuy, margin: "0 0 12px", fontSize: 11, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 };
  const divider      = { borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 14, marginBottom: 14 };

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.72)", zIndex: 1100, backdropFilter: "blur(4px)" }} />
      <div style={{
        position: "fixed", top: "50%", left: "50%",
        transform: "translate(-50%, -50%)",
        width: "min(580px, 94vw)", maxHeight: "85vh", overflowY: "auto",
        backgroundColor: SEMANTIC.backgroundCard,
        borderRadius: 16, boxShadow: "0 20px 60px rgba(0,0,0,0.8)",
        zIndex: 1101, padding: "24px 24px 20px",
      }}>
        {/* close */}
        <button onClick={onClose} style={{ position: "absolute", top: 14, right: 14, background: "none", border: "none", color: SEMANTIC.textSecondary, fontSize: 20, cursor: "pointer", lineHeight: 1, padding: 4 }}>✕</button>

        {/* header */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, paddingRight: 32 }}>
          <h3 style={{ color: SEMANTIC.ctaPrimary, margin: 0, fontSize: 18 }}>{tipo.Nome_tipo_modello}</h3>
          <AssetTypeBadge universe={tipo.ticker_universe} />
        </div>

        {/* descrizione completa */}
        <p style={{ color: SEMANTIC.textSecondary, fontSize: 13, lineHeight: 1.6, marginBottom: 16 }}>
          {tipo.Descrizione_tipo_modello}
        </p>

        {/* indicatori scala */}
        <div style={divider}>
          <h4 style={sectionHead}>Indicatori</h4>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {[
              ["Amplificazione",    tipo.amplificazione, 5, SEMANTIC.actionBuy],
              ["Resistenza",        tipo.resistenza,     5, SEMANTIC.ctaPrimary],
              ["Orizzonte temp.",   tipo.intervallo_tempo, 5, SEMANTIC.actionHold],
            ].map(([label, val, max, color]) => (
              <div key={label}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ fontSize: 12, color: SEMANTIC.textSecondary }}>{label}</span>
                  <span style={{ fontSize: 12, color, fontWeight: 700 }}>
                    {label === "Orizzonte temp." ? (INTERVALLO_MODAL[val] || "—") : `${val}/5`}
                  </span>
                </div>
                <ScaleIndicator value={val} max={max} color={color} />
              </div>
            ))}
          </div>
        </div>

        {/* investimento minimo */}
        {tipo.min_investment != null && (
          <div style={{ marginBottom: 14, padding: "9px 14px", backgroundColor: "rgba(255,255,255,0.04)", borderRadius: 8, border: "1px solid rgba(255,255,255,0.08)", display: "flex", justifyContent: "space-between" }}>
            <span style={{ color: SEMANTIC.textSecondary, fontSize: 12 }}>Investimento minimo</span>
            <span style={{ color: SEMANTIC.ctaPrimary, fontWeight: 700, fontSize: 12 }}>${Number(tipo.min_investment).toLocaleString("it-IT")}</span>
          </div>
        )}

        {/* performance grid */}
        {hasPerfData && (
          <div style={divider}>
            <h4 style={sectionHead}>Performance</h4>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {PERF_PERIODS_MODAL.map(([label, perfKey, diffKey]) => {
                const perf = tipo[perfKey] != null ? Number(tipo[perfKey]) : null;
                const diff = tipo[diffKey] != null ? Number(tipo[diffKey]) : null;
                if (perf == null && diff == null) return null;
                const pc = v => v != null ? (v >= 0 ? SEMANTIC.actionBuy : SEMANTIC.actionSell) : "#777";
                const fmt = v => v != null ? `${v >= 0 ? "+" : ""}${v.toFixed(2)}%` : "N/A";
                return (
                  <div key={label} style={{ backgroundColor: "rgba(0,0,0,0.28)", padding: "9px 10px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.06)" }}>
                    <div style={{ color: "#00aaff", fontSize: 11, fontWeight: 700, marginBottom: 4 }}>{label}</div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
                      <span style={{ color: SEMANTIC.textSecondary, fontSize: 11 }}>Portafoglio</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: pc(perf) }}>{fmt(perf)}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ color: SEMANTIC.textSecondary, fontSize: 11 }}>vs S&P500</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: pc(diff) }}>{fmt(diff)}</span>
                    </div>
                  </div>
                );
              }).filter(Boolean)}
            </div>
          </div>
        )}

        {/* indicatori tecnici */}
        {hasIndData && (
          <div style={divider}>
            <h4 style={sectionHead}>Indicatori tecnici</h4>
            <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
              {INDICATOR_DEFS_MODAL.map(([key, label, type]) => {
                const raw = tipo[key];
                if (raw == null) return null;
                const val = parseFloat(raw);
                let display, color;
                if (type === "alpha")  { display = `${(val * 100).toFixed(2)}%`; color = val > 0 ? "#00ff88" : "#ff5555"; }
                else if (type === "beta") { display = val.toFixed(3); color = val < 0.8 ? "#00ff88" : val <= 1.2 ? "#ffaa00" : "#ff5555"; }
                else { display = val.toFixed(4); color = val >= 1.5 ? "#00ff88" : val >= 0.5 ? "#ffaa00" : val >= 0 ? "#889" : "#ff5555"; }
                return (
                  <div key={key} style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ color: SEMANTIC.textSecondary, fontSize: 12 }}>{label}</span>
                    <span style={{ color, fontSize: 12, fontWeight: 700 }}>{display}</span>
                  </div>
                );
              }).filter(Boolean)}
            </div>
          </div>
        )}

        {/* link grafico */}
        {tipo.link_grafico && (
          <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 14 }}>
            <a href={tipo.link_grafico} target="_blank" rel="noopener noreferrer"
              style={{ display: "inline-flex", alignItems: "center", gap: 6, color: SEMANTIC.actionBuy, fontSize: 13, textDecoration: "none" }}>
              <i className="bi bi-graph-up" /> Vedi grafico simulazione →
            </a>
          </div>
        )}
      </div>
    </>
  );
}

// ======================================================
// Etichette intervallo tempo
// ======================================================
const INTERVALLO_LABELS = {
  1: "3 mesi",
  2: "6 mesi",
  3: "12 mesi",
  4: "24 mesi",
  5: "36+ mesi",
};

export default function PortfolioTypeSelector() {
  const userId = localStorage.getItem("userId");
  const [tipi, setTipi] = useState([]);
  const [selected, setSelected] = useState(null);
  const [importo, setImporto] = useState("");
  const [esistenti, setEsistenti] = useState([]);
  const [alpacaCash, setAlpacaCash] = useState(null);
  const [alpacaLoading, setAlpacaLoading] = useState(true);
  const [portfolioMode, setPortfolioMode] = useState('paper'); // letto dal profilo utente
  const [membershipTier, setMembershipTier] = useState(null);
  const [mrkOpen, setMrkOpen] = useState(false);
  const [detailModal, setDetailModal] = useState(null);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { modal, showModal } = useAppModal();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [tipiRes, portfoliosRes, alpacaRes, profileRes] = await Promise.all([
          axios.get("/api/tipo_portafoglio"),
          axios.get(`/api/portfolios/${userId}`),
          axios.get(`/api/alpaca/account?user_id=${userId}`).catch(() => null),
          axios.get(`/api/users/${userId}/profile`).catch(() => null),
        ]);

        const tipiData = Array.isArray(tipiRes.data)
          ? tipiRes.data
          : tipiRes.data.data || [];

        const portfoliosData = Array.isArray(portfoliosRes.data)
          ? portfoliosRes.data
          : portfoliosRes.data.portfolios || [];

        const mode = profileRes?.data?.trading_mode || 'paper';
        setPortfolioMode(mode);
        setTipi(tipiData);

        const membership = profileRes?.data?.membership || 'earth';
        const tierRes = await axios.get('/api/membership-tiers').catch(() => null);
        const tiers = tierRes?.data || [];
        const myTier = tiers.find(t => t.tier_key === membership) || null;
        setMembershipTier(myTier);

        // Un tipo è "già creato" solo se esiste un portafoglio con lo stesso trading_mode corrente
        setEsistenti(
          portfoliosData
            .filter(p => (p.trading_mode || 'paper') === mode)
            .map(p => p.tipo_id)
        );

        if (alpacaRes?.data?.cash !== undefined) {
          setAlpacaCash(Number(alpacaRes.data.cash));
        }
      } catch (error) {
        showModal({ type: 'error', title: t("portfolio_type_selector.alert_load_error") });
      } finally {
        setAlpacaLoading(false);
      }
    };

    fetchData();
  }, [userId, t, showModal]); // eslint-disable-line react-hooks/exhaustive-deps

  const importoNum = parseFloat(importo) || 0;
  const exceedsCash = alpacaCash !== null && importoNum > alpacaCash;
  const minInvestment = selected?.min_investment ?? 10000;

  function isLockedByMembership(tipo) {
    // Il paper trading usa il profilo Saturn: nessun tipo è bloccato
    if (portfolioMode === 'paper') return false;
    if (!membershipTier) return false;
    const allowed = Array.isArray(membershipTier.allowed_assets) ? membershipTier.allowed_assets : [];
    if (allowed.some(a => a.toUpperCase() === 'ALL')) return false;
    const uni = (tipo.ticker_universe || 'ALL').toUpperCase();
    return !allowed.some(a => a.toUpperCase() === uni);
  }

  const handleCreate = async () => {
    if (!selected) { showModal({ type: 'warning', title: t("portfolio_type_selector.alert_select") }); return; }
    if (esistenti.includes(selected.tipo_id)) { showModal({ type: 'warning', title: t("portfolio_type_selector.alert_already_exists") }); return; }
    if (importoNum < minInvestment) { showModal({ type: 'warning', title: t("portfolio_type_selector.alert_min_amount", { amount: minInvestment.toLocaleString('it-IT') }) }); return; }
    if (exceedsCash) {
      showModal({ type: 'warning', title: t("portfolio_type_selector.alert_exceeds_balance", `L'importo supera il saldo disponibile su Alpaca ($${alpacaCash.toLocaleString("en-US", { minimumFractionDigits: 2 })})`) });
      return;
    }

    try {
      await axios.post("/api/portfolio", {
        user_id: userId,
        tipo_id: selected.tipo_id,
        budget: parseFloat(importo),
        trading_mode: portfolioMode,
      });
      showModal({
        type: 'success',
        title: t("portfolio_type_selector.alert_success"),
        onConfirm: () => navigate("/portfolio"),
        confirmLabel: t('common.continue'),
      });
    } catch (err) {
      showModal({ type: 'error', title: t("portfolio_type_selector.alert_error") });
    }
  };

  return (
    <>
    {modal}
    <div
      style={{
        backgroundColor: SEMANTIC.backgroundApp,
        color: SEMANTIC.textPrimary,
        minHeight: "100vh",
        padding: "40px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <div style={{ width: "100%", maxWidth: "1100px" }}>
        <BackButton to="/portfolio" />
      </div>

      <div style={{ textAlign: "center", marginBottom: "30px" }}>
        <h2 style={{ color: SEMANTIC.ctaPrimary, margin: "0 0 14px", fontSize: "28px", fontWeight: "bold" }}>
          {t("portfolio_type_selector.title")}
        </h2>
        <button
          onClick={() => setMrkOpen(true)}
          style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            padding: "9px 20px", borderRadius: 24,
            backgroundColor: "rgba(255,215,0,0.08)",
            border: "1px solid rgba(255,215,0,0.35)",
            color: "#ffd700", fontSize: 13, fontWeight: 600,
            cursor: "pointer", whiteSpace: "nowrap",
            transition: "background-color 0.2s",
          }}
          onMouseOver={e => e.currentTarget.style.backgroundColor = "rgba(255,215,0,0.15)"}
          onMouseOut={e => e.currentTarget.style.backgroundColor = "rgba(255,215,0,0.08)"}
        >
          <span style={{ fontSize: 16 }}>🪐</span>
          {t('portfolio_type_selector.mrk_button')}
        </button>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          gap: "12px",
          width: "100%",
          maxWidth: "1100px",
        }}
      >
        {(Array.isArray(tipi) ? [...tipi].sort((a, b) => {
          const rank = (t) => {
            if (!t.selezionabile || isLockedByMembership(t)) return 2;
            if (esistenti.includes(t.tipo_id)) return 1;
            return 0;
          };
          return rank(a) - rank(b);
        }) : []).map((tipo) => {
          const giàCreato = esistenti.includes(tipo.tipo_id);
          const nonSelezionabile = !tipo.selezionabile;
          const lockedByMembership = isLockedByMembership(tipo);
          const disabilitato = giàCreato || nonSelezionabile || lockedByMembership;
          const isSelected = selected?.tipo_id === tipo.tipo_id;

          // Colori indicatori: spenti se non selezionabile
          const ampColor  = nonSelezionabile ? SEMANTIC.textDisabled : SEMANTIC.actionBuy;
          const resColor  = nonSelezionabile ? SEMANTIC.textDisabled : SEMANTIC.ctaPrimary;
          const timeColor = nonSelezionabile ? SEMANTIC.textDisabled : SEMANTIC.actionHold;
          const nameColor = nonSelezionabile ? SEMANTIC.textDisabled : SEMANTIC.ctaPrimary;

          return (
            <div
              key={tipo.tipo_id}
              onClick={() => { if (!disabilitato) setSelected(tipo); }}
              style={{
                backgroundColor: nonSelezionabile
                  ? "rgba(255,255,255,0.02)"
                  : isSelected
                  ? `color-mix(in srgb, ${SEMANTIC.backgroundCard} 80%, ${SEMANTIC.ctaPrimary} 20%)`
                  : SEMANTIC.backgroundCard,
                border: `2px ${nonSelezionabile ? "dashed" : "solid"} ${
                  nonSelezionabile
                    ? SEMANTIC.actionHold
                    : giàCreato
                    ? SEMANTIC.textDisabled
                    : isSelected
                    ? SEMANTIC.ctaPrimary
                    : "rgba(255,255,255,0.12)"
                }`,
                borderRadius: "10px",
                padding: "12px 14px",
                cursor: disabilitato ? "not-allowed" : "pointer",
                opacity: giàCreato ? 0.5 : 1,
                position: "relative",
                boxShadow: isSelected
                  ? `0 0 16px ${SEMANTIC.ctaPrimary}33`
                  : "0 4px 8px rgba(0,0,0,0.4)",
                transition: "border 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
              }}
            >
              {/* Badge già creato */}
              {giàCreato && (
                <div
                  style={{
                    position: "absolute",
                    top: "8px",
                    right: "8px",
                    backgroundColor: SEMANTIC.actionSell,
                    color: "#fff",
                    padding: "2px 6px",
                    borderRadius: "4px",
                    fontSize: "11px",
                    fontWeight: "bold",
                  }}
                >
                  {t("portfolio_type_selector.already_created")}
                </div>
              )}

              {/* Badge non adatto al profilo */}
              {nonSelezionabile && !giàCreato && (
                <div
                  style={{
                    position: "absolute",
                    top: "8px",
                    right: "8px",
                    backgroundColor: `${SEMANTIC.actionHold}22`,
                    color: SEMANTIC.actionHold,
                    border: `1px solid ${SEMANTIC.actionHold}`,
                    padding: "2px 6px",
                    borderRadius: "4px",
                    fontSize: "10px",
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  ⚠ Non adatto al profilo
                </div>
              )}

              {/* Badge piano non sufficiente */}
              {lockedByMembership && !giàCreato && !nonSelezionabile && (
                <div style={{
                  position: 'absolute', top: '8px', right: '8px',
                  backgroundColor: 'rgba(255,170,0,0.15)',
                  color: '#ffaa00',
                  border: '1px solid #ffaa00',
                  padding: '2px 6px', borderRadius: '4px',
                  fontSize: '10px', fontWeight: 'bold',
                }}>
                  🔒 Piano non sufficiente
                </div>
              )}

              {/* Nome e descrizione */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, paddingRight: nonSelezionabile ? "130px" : "0" }}>
                <h3 style={{ color: nameColor, margin: 0, fontSize: 15 }}>
                  {tipo.Nome_tipo_modello}
                </h3>
                <AssetTypeBadge universe={tipo.ticker_universe} size="sm" />
              </div>
              <p style={{ color: nonSelezionabile ? SEMANTIC.textDisabled : SEMANTIC.textSecondary, fontSize: "12px", lineHeight: "1.4", marginBottom: "10px" }}>
                {tipo.Descrizione_tipo_modello
                  ? tipo.Descrizione_tipo_modello.split(" ").slice(0, 20).join(" ") + "..."
                  : ""}
              </p>

              {/* Indicatori grafici */}
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "2px" }}>
                    <span style={{ fontSize: "11px", color: SEMANTIC.textSecondary }}>Amplificazione</span>
                    <span style={{ fontSize: "11px", color: ampColor, fontWeight: "bold" }}>
                      {tipo.amplificazione}/5
                    </span>
                  </div>
                  <ScaleIndicator value={tipo.amplificazione} max={5} color={ampColor} />
                </div>

                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "2px" }}>
                    <span style={{ fontSize: "11px", color: SEMANTIC.textSecondary }}>Resistenza</span>
                    <span style={{ fontSize: "11px", color: resColor, fontWeight: "bold" }}>
                      {tipo.resistenza}/5
                    </span>
                  </div>
                  <ScaleIndicator value={tipo.resistenza} max={5} color={resColor} />
                </div>

                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "2px" }}>
                    <span style={{ fontSize: "11px", color: SEMANTIC.textSecondary }}>Orizzonte temporale</span>
                    <span style={{ fontSize: "11px", color: timeColor, fontWeight: "bold" }}>
                      {INTERVALLO_LABELS[tipo.intervallo_tempo] || "-"}
                    </span>
                  </div>
                  <ScaleIndicator value={tipo.intervallo_tempo} max={5} color={timeColor} />
                </div>
              </div>

              {/* Avviso profilo non adatto */}
              {nonSelezionabile && !giàCreato && (
                <div
                  style={{
                    marginTop: "8px",
                    padding: "6px 8px",
                    backgroundColor: `${SEMANTIC.actionHold}14`,
                    border: `1px solid ${SEMANTIC.actionHold}44`,
                    borderRadius: "6px",
                    fontSize: "11px",
                    color: SEMANTIC.actionHold,
                    lineHeight: "1.4",
                  }}
                >
                  Questo portafoglio non è compatibile con il tuo profilo di rischio MiFID. Contatta il tuo consulente per maggiori informazioni.
                </div>
              )}

              {/* Bottone dettagli */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setDetailModal(tipo);
                }}
                style={{
                  marginTop: "8px",
                  background: "none",
                  border: `1px solid ${SEMANTIC.ctaPrimary}55`,
                  color: SEMANTIC.ctaPrimary,
                  cursor: "pointer",
                  fontSize: "11px",
                  padding: "3px 9px",
                  borderRadius: "4px",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <i className="bi bi-info-circle" style={{ fontSize: 11 }} />
                {t("portfolio_type_selector.details_link")}
              </button>
            </div>
          );
        })}
      </div>

      {selected && (
        <div
          style={{
            marginTop: "40px",
            backgroundColor: SEMANTIC.backgroundCard,
            padding: "20px",
            borderRadius: "10px",
            width: "100%",
            maxWidth: "500px",
            boxShadow: "0 4px 8px rgba(0,0,0,0.6)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <h3 style={{ color: SEMANTIC.ctaPrimary, marginBottom: "10px" }}>
            {t("portfolio_type_selector.selected_type", { name: selected.Nome_tipo_modello })}
          </h3>

          {/* Modalità attiva (read-only, viene dalle impostazioni) */}
          {(() => {
            const isReal = portfolioMode === 'real';
            const modeColor = isReal ? '#00aaff' : '#00ff88';
            return (
              <div style={{
                width: "80%", marginBottom: 12,
                padding: "10px 14px", borderRadius: 8,
                backgroundColor: `${modeColor}0d`,
                border: `1px solid ${modeColor}44`,
                display: "flex", alignItems: "center", justifyContent: "space-between",
              }}>
                <span style={{ fontSize: 13, color: modeColor, fontWeight: 600 }}>
                  {isReal ? '🏦 Real Trading' : '🧪 Paper Trading'}
                </span>
                <span style={{ fontSize: 11, color: "#555" }}>
                  {t("portfolio_type_selector.mode_from_settings", "da Impostazioni")}
                </span>
              </div>
            );
          })()}

          {/* Saldo disponibile */}
          <div style={{
            width: "80%",
            marginBottom: "10px",
            padding: "8px 12px",
            borderRadius: "6px",
            backgroundColor: alpacaCash !== null ? "rgba(0,255,136,0.08)" : "rgba(255,255,255,0.03)",
            border: `1px solid ${alpacaCash !== null ? "rgba(0,255,136,0.3)" : "rgba(255,255,255,0.08)"}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}>
            <span style={{ fontSize: 12, color: "#888" }}>
              {portfolioMode === 'real'
                ? t("portfolio_type_selector.alpaca_balance_real", "Saldo Alpaca reale")
                : t("portfolio_type_selector.alpaca_balance_paper", "Saldo paper trading")}
            </span>
            <span style={{ fontSize: 14, fontWeight: 700, color: "#00ff88" }}>
              {alpacaLoading
                ? "…"
                : alpacaCash !== null
                  ? `$${alpacaCash.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                  : "—"}
            </span>
          </div>

          <input
            type="number"
            value={importo}
            onChange={(e) => setImporto(e.target.value)}
            placeholder={t("portfolio_type_selector.min_amount", { amount: minInvestment.toLocaleString('it-IT') })}
            style={{
              width: "80%",
              padding: "10px",
              borderRadius: "5px",
              border: `1px solid ${exceedsCash ? "rgba(255,100,100,0.5)" : "rgba(255,255,255,0.2)"}`,
              backgroundColor: "rgba(255,255,255,0.07)",
              color: SEMANTIC.textPrimary,
              marginBottom: exceedsCash ? "4px" : "15px",
            }}
          />
          {/* Errore: importo supera il saldo */}
          {exceedsCash && (
            <div style={{
              width: "80%",
              marginBottom: "15px",
              padding: "6px 10px",
              borderRadius: "5px",
              backgroundColor: "rgba(255,100,100,0.1)",
              border: "1px solid rgba(255,100,100,0.3)",
              fontSize: 12,
              color: "#ff6b6b",
            }}>
              ⚠ {t("portfolio_type_selector.warn_exceeds", "L'importo supera il saldo disponibile su Alpaca")}
            </div>
          )}

          <button
            onClick={handleCreate}
            disabled={exceedsCash}
            style={{
              backgroundColor: exceedsCash ? "rgba(255,255,255,0.05)" : SEMANTIC.ctaPrimary,
              border: "none",
              color: exceedsCash ? SEMANTIC.textDisabled : SEMANTIC.ctaPrimaryText,
              fontWeight: "bold",
              padding: "10px 20px",
              borderRadius: "5px",
              cursor: exceedsCash ? "not-allowed" : "pointer",
              width: "80%",
              fontSize: "16px",
            }}
          >
            {t("portfolio_type_selector.create_button")}
          </button>
        </div>
      )}
    </div>

    <MrKDrawer open={mrkOpen} onClose={() => setMrkOpen(false)} />
    {detailModal && <PortfolioDetailModal tipo={detailModal} onClose={() => setDetailModal(null)} />}
    </>
  );
}
